import { useState, useEffect, useCallback, useRef } from 'react';
import apiService, { leaguesAPI, playersAPI } from '../../../../../services/apiService';

/**
 * usePlayerData Hook
 * 
 * Fetches complete player data for the modal.
 * Uses client-side caching to avoid redundant API calls.
 * 
 * @param {string} playerId - MLB player ID
 * @param {string} leagueId - League ID for context
 * @param {boolean} enabled - Whether to fetch data (default: true)
 * 
 * @returns {object} { data, loading, error, refetch }
 * 
 * @example
 * const { data, loading, error, refetch } = usePlayerData('545361', 'league_123');
 */

// Session-level cache (persists until page refresh) — extracted to the
// dependency-free playerDataCache module (DD-MODAL-0723) so mutation
// surfaces can invalidate it without importing this fetch hook. The
// invalidators are re-exported here for modal-tree consumers.
import {
  playerCache,
  cacheTimestamps,
  CACHE_TTL,
  invalidatePlayer,
  invalidateLeaguePlayers,
} from './playerDataCache';

export { invalidatePlayer, invalidateLeaguePlayers };

const usePlayerData = (playerId, leagueId, enabled = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  // Track if component is mounted to avoid state updates after unmount
  const isMounted = useRef(true);
  // Track the cacheKey of the player currently being viewed. Every request (and
  // retry) captures the key it was issued for; on resolution we commit state only
  // if it still matches this ref — so a slow response for a previously-viewed
  // player can never overwrite the current player's modal (in-flight-race guard).
  const currentKeyRef = useRef(`${leagueId}-${playerId}`);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // Base delay in ms

  // Generate cache key
  const cacheKey = `${leagueId}-${playerId}`;

  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    if (!playerCache.has(cacheKey)) return false;
    
    const timestamp = cacheTimestamps.get(cacheKey);
    if (!timestamp) return false;
    
    const age = Date.now() - timestamp;
    return age < CACHE_TTL;
  }, [cacheKey]);

  // Fetch player data from API
  const fetchPlayerData = useCallback(async () => {
    // Don't fetch if disabled or missing playerId
    if (!enabled || !playerId) {
      return;
    }

    // Capture the key this request is issued for; on every resolution below we
    // commit state only if it still matches the player currently being viewed,
    // so a slow/retried response for a prior player cannot overwrite the new one.
    const issuedKey = cacheKey;
    const stillCurrent = () => isMounted.current && issuedKey === currentKeyRef.current;

    // Check cache first
    console.log("🔍 fetchPlayerData called:", { playerId, leagueId, enabled });
    if (isCacheValid()) {
      console.log(`✅ Using cached data for player ${playerId}`);
      const cachedData = playerCache.get(cacheKey);
      if (stillCurrent()) {
        setData(cachedData);
        setLoading(false);
        setError(null);
      }
      return;
    }

    // Fetch from API
    setLoading(true);
    setError(null);

    try {
      console.log(`📡 Fetching player data: ${playerId} (league: ${leagueId || 'global'}) [Attempt ${retryCount + 1}]`);
      
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      let response;
      // Use /modal endpoint for league context (includes analytics), canonical for global
      if (!leagueId) {
        // Global player endpoint - returns canonical structure
        response = await apiService.get(`/api/players/${playerId}`, { signal: controller.signal });
      } else {
        // Modal endpoint - returns canonical structure with league_context + analytics
        response = await apiService.get(`/api/players/leagues/${leagueId}/players/${playerId}/modal`, { signal: controller.signal });
      }
      clearTimeout(timeoutId);
      
      console.log("🔍 API Response:", response);
      
      // Extract player data from response
      const playerData = response.data?.player || response.player;
      
      if ((response.success || response.data?.success) && playerData) {
        // Attach top-level fields that are siblings of `player` in the response
        const statsSeasonYear = response.data?.stats_season_year || response.stats_season_year;
        if (statsSeasonYear != null) {
          playerData.stats_season_year = statsSeasonYear;
        }

        // NO TRANSFORMATION - use canonical structure directly
        // Cache the canonical data
        playerCache.set(cacheKey, playerData);
        cacheTimestamps.set(cacheKey, Date.now());

        console.log(`✅ Player data fetched successfully for ${playerId}`);

        if (stillCurrent()) {
          setData(playerData);
          setLoading(false);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch player data');
      }
    } catch (err) {
      console.error(`❌ Error fetching player data for ${playerId}:`, err);
      
      // Determine error type for better messaging
      let errorMessage = 'Failed to load player data';
      let shouldRetry = false;
      
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try again.';
        shouldRetry = true;
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network.';
      } else if (err.message?.includes('404') || err.message?.includes('not found')) {
        errorMessage = 'Player not found.';
      } else if (err.message?.includes('500') || err.message?.includes('Internal Server Error')) {
        errorMessage = 'Server error. Please try again later.';
        shouldRetry = true;
      } else if (err.message?.includes('Failed to fetch') || err.message?.includes('Network request failed')) {
        errorMessage = 'Network error. Please check your connection.';
        shouldRetry = true;
      } else {
        errorMessage = err.message || 'Failed to load player data';
        shouldRetry = true;
      }
      
      // Auto-retry with exponential backoff (up to MAX_RETRIES)
      if (shouldRetry && retryCount < MAX_RETRIES && stillCurrent()) {
        const delay = RETRY_DELAY * Math.pow(2, retryCount); // Exponential backoff
        console.log(`🔄 Retrying in ${delay}ms... (Attempt ${retryCount + 2}/${MAX_RETRIES + 1})`);
        
        setTimeout(() => {
          if (stillCurrent()) {
            setRetryCount(retryCount + 1);
            fetchPlayerData();
          }
        }, delay);
      } else {
        // Max retries reached or shouldn't retry
        if (stillCurrent()) {
          setError(errorMessage);
          setLoading(false);
          setData(null);
          setRetryCount(0);
        }
      }
    }
  }, [playerId, leagueId, enabled, cacheKey, isCacheValid, retryCount]);

  // DELETED: transformPlayerData() function - components now use canonical structure directly
  // All mobile components access: player.info.name, player.stats.season.hitting, etc.

  // Manual refetch (bypasses cache and resets retry count)
  const refetch = useCallback(() => {
    console.log(`🔄 Forcing refetch for player ${playerId}`);
    // Clear cache for this player
    playerCache.delete(cacheKey);
    cacheTimestamps.delete(cacheKey);
    // Reset retry count
    setRetryCount(0);
    // Fetch fresh data
    fetchPlayerData();
  }, [playerId, cacheKey, fetchPlayerData]);

  // Reset state on player change so the previous player's data never bleeds into
  // the new player's modal, and point currentKeyRef at the player now being viewed
  // (the in-flight-race guard). If the new player is a valid cache hit, show it
  // immediately; otherwise clear to the loading state. Runs before the fetch
  // effect below (declaration order), which stays responsible for the actual
  // fetch — so data is cleared once here, never double-fetched.
  useEffect(() => {
    currentKeyRef.current = cacheKey;
    if (!enabled || !playerId) return;
    if (isCacheValid()) {
      setData(playerCache.get(cacheKey));
      setError(null);
      setLoading(false);
    } else {
      setData(null);      // <-- the fix: no stale bleed
      setError(null);
      setLoading(true);
    }
    setRetryCount(0);
    // isCacheValid only changes with cacheKey (already a dep), so it adds no extra
    // trigger — listed to satisfy exhaustive-deps. Effect fires on every player switch.
  }, [cacheKey, enabled, playerId, isCacheValid]);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    isMounted.current = true;
    fetchPlayerData();
    
    return () => {
      isMounted.current = false;
    };
  }, [fetchPlayerData]);

  return {
    data,
    loading,
    error,
    refetch
  };
};

export default usePlayerData;
